import { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import { MoneyInput } from '../components/MoneyInput';
import { PrivacyBanner } from '../components/PrivacyBanner';
import { Banner } from '../ui';
import { todayIso, fmtMoney, relativeDate } from '../utils/format';
import { uuid } from '../utils/uuid';
import type { Account, Snapshot } from '../types';

interface Props {
  onDone: () => void;
}

/**
 * Fast-path snapshot entry.
 *
 * Two modes, keyed off whether the profile has any prior snapshots:
 *
 * - First-visit mode (no snapshots yet): every checkbox defaults to
 *   UNCHECKED and a hint banner explains that everything is optional.
 *   The primary button routes the visitor to the dashboard without
 *   writing anything when nothing is checked. A "Skip for now" escape
 *   hatch sits next to it. This exists because the initial catalog of
 *   accounts (HYSA, Checking, 401(k), Taxable, Home, Mortgage) looks
 *   like a required intake form when every row is pre-checked; opting
 *   in per row makes the "all optional" affordance visible.
 *
 * - Returning-user mode (>=1 snapshot): checkboxes default to CHECKED
 *   (the user came here to update balances against a known catalog);
 *   the primary button reads "Save N changes" / "Nothing changed" per
 *   the sparse-snapshot rule.
 */
export function UpdatePage({ onDone }: Props) {
  const { accounts, snapshots, currentBalances, saveSnapshot } = useStore();
  const firstVisit = snapshots.length === 0;

  const [date, setDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [values, setValues] = useState<Record<string, number>>(() => ({ ...currentBalances }));
  const [includeMap, setIncludeMap] = useState<Record<string, boolean>>(() =>
    initialIncludeMap(accounts, firstVisit),
  );
  const [saving, setSaving] = useState(false);

  const lastSeenDateByAccount = useMemo(() => {
    const m: Record<string, string> = {};
    const sortedAsc = [...snapshots].sort((a, b) => (a.date < b.date ? -1 : 1));
    for (const s of sortedAsc) {
      for (const k of Object.keys(s.balances)) m[k] = s.date;
    }
    return m;
  }, [snapshots]);

  const total = useMemo(() => {
    let inv = 0;
    let home = 0;
    let liab = 0;
    for (const a of accounts) {
      if (!includeMap[a.id]) continue;
      const v = values[a.id] ?? 0;
      if (a.kind === 'home') home += v;
      else if (a.kind === 'mortgage' || a.kind === 'other_liability') liab += v;
      else inv += v;
    }
    return { inv, home, liab, nw: inv + home - liab };
  }, [accounts, includeMap, values]);

  const checkedCount = useMemo(
    () => accounts.filter(a => includeMap[a.id]).length,
    [accounts, includeMap],
  );

  // Snapshots are sparse (per CLAUDE.md): a Snapshot records only the
  // accounts whose values actually changed. Unchanged accounts are
  // forward-filled by latestBalances().
  //
  // In first-visit mode we relax the "must differ from prior" rule:
  // priors are all 0, so a user who checks "HYSA" and types nothing has
  // no delta — yet clearly wants that account persisted (they said they
  // own it). Persist every checked account on the first snapshot, even
  // at $0; a returning user still gets the strict sparse-delta behavior.
  const savableIds = useMemo(() => {
    return accounts
      .filter(a => includeMap[a.id])
      .filter(a => firstVisit || (values[a.id] ?? 0) !== (currentBalances[a.id] ?? 0))
      .map(a => a.id);
  }, [accounts, includeMap, values, currentBalances, firstVisit]);

  const primary = primaryButtonState({
    firstVisit,
    checkedCount,
    savableCount: savableIds.length,
    saving,
  });

  async function handlePrimary() {
    if (primary.mode === 'route-only') {
      onDone();
      return;
    }
    if (primary.mode === 'noop') return;
    setSaving(true);
    try {
      const balances: Record<string, number> = {};
      for (const id of savableIds) balances[id] = values[id] ?? 0;
      const snap: Snapshot = {
        id: uuid(),
        date,
        balances,
        notes: notes.trim() || undefined,
        createdAt: new Date().toISOString(),
      };
      await saveSnapshot(snap);
      onDone();
    } finally {
      setSaving(false);
    }
  }

  const groups: { title: string; items: Account[] }[] = [
    { title: 'Assets', items: accounts.filter(a => !isLiability(a)) },
    { title: 'Liabilities', items: accounts.filter(a => isLiability(a)) },
  ].filter(g => g.items.length > 0);

  return (
    <>
      <TopBar
        title="Update balances"
        subtitle="Enter today's values; we'll compute the rest."
        right={
          <button className="btn btn-ghost text-sm" onClick={onDone}>
            Cancel
          </button>
        }
      />
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-4 pb-nav">
        <PrivacyBanner />
        {firstVisit && (
          <Banner tone="info" title="Everything here is optional">
            Check the accounts you actually have, enter today's balance, and
            skip the rest. Cairn only saves the rows you touch — you can
            always come back and add more later.
          </Banner>
        )}
        <section className="card card-dark p-4">
          <label className="label">Date</label>
          <input
            type="date"
            className="input mt-1"
            value={date}
            max={todayIso()}
            onChange={e => setDate(e.target.value)}
          />
        </section>

        {groups.map(g => (
          <section key={g.title} className="card card-dark">
            <div className="border-b border-ink-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:border-ink-700 dark:text-ink-300">
              {g.title}
            </div>
            <ul>
              {g.items.map(a => {
                const last = lastSeenDateByAccount[a.id];
                const prior = currentBalances[a.id] ?? 0;
                const delta = (values[a.id] ?? 0) - prior;
                const included = includeMap[a.id] ?? false;
                const hasHistory = Boolean(last);
                return (
                  <li
                    key={a.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-ink-100 last:border-b-0 dark:border-ink-700 ${
                      !included ? 'opacity-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-3 h-4 w-4 accent-accent-600"
                      checked={included}
                      onChange={e => setIncludeMap(m => ({ ...m, [a.id]: e.target.checked }))}
                      aria-label={`Include ${a.name}`}
                    />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold">{a.name}</div>
                          <div className="text-[11px] text-ink-500 dark:text-ink-300">
                            {kindLabel(a)}
                            {hasHistory && <> · last {relativeDate(last)}</>}
                          </div>
                        </div>
                        {hasHistory && (
                          <div className="shrink-0 text-[11px] text-ink-500 dark:text-ink-300">
                            prior {fmtMoney(prior)}
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1">
                          <MoneyInput
                            value={values[a.id] ?? 0}
                            onChange={v => {
                              setValues(vs => ({ ...vs, [a.id]: v }));
                              // Typing a value in first-visit mode is a natural
                              // opt-in — auto-check the row so the value counts.
                              if (firstVisit && !included) {
                                setIncludeMap(m => ({ ...m, [a.id]: true }));
                              }
                            }}
                            aria-label={a.name}
                          />
                        </div>
                        {included && hasHistory && Math.abs(delta) > 0 && (
                          <span
                            className={`font-mono text-xs ${
                              delta > 0 ? 'text-pos-700 dark:text-pos-500' : 'text-neg-700 dark:text-neg-500'
                            }`}
                          >
                            {delta > 0 ? '+' : '−'}
                            {fmtMoney(Math.abs(delta))}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        <section className="card card-dark p-4">
          <label className="label">Notes (optional)</label>
          <textarea
            className="input mt-1 h-20 resize-none"
            placeholder="e.g. got quarterly bonus, refi'd mortgage…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </section>

        <section className="card card-dark p-4">
          <div className="flex items-baseline justify-between">
            <span className="label">New net worth</span>
            <span className="font-mono text-xl font-bold tabular-nums">{fmtMoney(total.nw)}</span>
          </div>
          <div className="mt-1 text-xs text-ink-500 dark:text-ink-300">
            Investable {fmtMoney(total.inv)} · Home {fmtMoney(total.home)} · Liab {fmtMoney(total.liab)}
          </div>
        </section>

        <div className="space-y-2">
          <button
            className="btn btn-primary w-full py-3 text-base"
            disabled={primary.disabled}
            onClick={handlePrimary}
          >
            {primary.label}
          </button>
          {firstVisit && (
            <button
              className="btn btn-ghost w-full py-2 text-sm text-ink-600 dark:text-ink-300"
              onClick={onDone}
            >
              Skip for now →
            </button>
          )}
        </div>
      </main>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Pure branch-logic helpers — exported for unit tests.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Compute the initial include-map for the checkboxes.
 *
 * - firstVisit=true: every account defaults to unchecked. The visitor
 *   opts in to the accounts they actually own; the rest visibly recede.
 * - firstVisit=false: every account defaults to checked (a returning
 *   user has the full known catalog and typically updates a few rows).
 */
export function initialIncludeMap(
  accounts: Pick<Account, 'id'>[],
  firstVisit: boolean,
): Record<string, boolean> {
  const m: Record<string, boolean> = {};
  for (const a of accounts) m[a.id] = !firstVisit;
  return m;
}

export interface PrimaryButtonInput {
  firstVisit: boolean;
  checkedCount: number;
  savableCount: number;
  saving: boolean;
}

export interface PrimaryButtonState {
  label: string;
  disabled: boolean;
  /** 'save' = write snapshot; 'route-only' = onDone() without saving; 'noop' = disabled. */
  mode: 'save' | 'route-only' | 'noop';
}

/**
 * Derive the primary-button label / disabled / action from the current
 * page state. Extracted so the label copy stays branch-testable without
 * needing a full React render.
 */
export function primaryButtonState({
  firstVisit,
  checkedCount,
  savableCount,
  saving,
}: PrimaryButtonInput): PrimaryButtonState {
  if (saving) {
    return { label: 'Saving…', disabled: true, mode: 'save' };
  }
  if (firstVisit) {
    if (checkedCount === 0) {
      return {
        label: 'Continue without balances →',
        disabled: false,
        mode: 'route-only',
      };
    }
    return {
      label: `Save ${checkedCount} account${checkedCount === 1 ? '' : 's'}`,
      disabled: savableCount === 0,
      mode: savableCount === 0 ? 'noop' : 'save',
    };
  }
  // Returning-user mode — preserve existing copy.
  if (savableCount === 0) {
    return { label: 'Nothing changed', disabled: true, mode: 'noop' };
  }
  return {
    label: `Save ${savableCount} change${savableCount === 1 ? '' : 's'}`,
    disabled: false,
    mode: 'save',
  };
}

function isLiability(a: Account): boolean {
  return a.kind === 'mortgage' || a.kind === 'other_liability';
}

function kindLabel(a: Account): string {
  switch (a.kind) {
    case 'hysa': return 'HYSA';
    case 'checking': return 'Checking';
    case 'taxable': return 'Taxable brokerage';
    case 'retirement': return 'Retirement';
    case 'home': return 'Home';
    case 'mortgage': return 'Mortgage';
    case 'other_asset': return 'Asset';
    case 'other_liability': return 'Liability';
  }
}
